import type {
  GlobalSearchApiResponse,
  GlobalSearchConversationItem,
} from '@saaga/shared/interfaces/global-search.interface'
import {
  BookOpenTextIcon,
  BuildingIcon,
  GlobeIcon,
  MessageSquareIcon,
  PenToolIcon,
} from 'lucide-react'

import type { GlobalSearchResultGroup, GlobalSearchResultItem } from './global-search.interface'

const transformAgencyItem = (agency: {
  slug: string
  presentation_name: string
  presentation_image_path: string | null
  presentation_description_short: string | null
}): GlobalSearchResultItem => ({
  id: agency.slug,
  title: agency.presentation_name,
  description: agency.presentation_description_short ?? undefined,
  imageUrl: agency.presentation_image_path,
})

const transformPromptTemplateItem = (template: {
  id: string
  name: string
  description: string
}): GlobalSearchResultItem => ({
  id: template.id,
  title: template.name,
  description: template.description,
  icon: BookOpenTextIcon,
})

const transformConversationItem = (
  conversation: GlobalSearchConversationItem
): GlobalSearchResultItem => ({
  id: conversation.id,
  title: conversation.name,
  icon: MessageSquareIcon,
  agencySlug: conversation.agency_slug,
})

/**
 * Transforms the global search API response into result groups for the search palette.
 * Filters out groups with empty item lists.
 */
export const transformSearchApiResponse = (
  data: GlobalSearchApiResponse
): GlobalSearchResultGroup[] => {
  const groupList: GlobalSearchResultGroup[] = [
    {
      type: 'marketplace_agency',
      label: 'Marketplace',
      itemList: data.marketplace_agency_list.map(transformAgencyItem),
    },
    {
      type: 'organization_agency',
      label: 'Organization Agencies',
      itemList: data.organization_agency_list.map(transformAgencyItem),
    },
    {
      type: 'draft_agency',
      label: 'Draft Agencies',
      itemList: data.draft_agency_list.map(transformAgencyItem),
    },
    {
      type: 'prompt_template',
      label: 'Prompt Templates',
      itemList: data.prompt_template_list.map(transformPromptTemplateItem),
    },
    {
      type: 'conversation',
      label: 'Conversations',
      itemList: data.conversation_list.map(transformConversationItem),
    },
  ]

  return groupList.filter((group) => group.itemList.length > 0)
}

export const SEARCH_GROUP_ICON_MAP = {
  marketplace_agency: GlobeIcon,
  organization_agency: BuildingIcon,
  draft_agency: PenToolIcon,
  prompt_template: BookOpenTextIcon,
  conversation: MessageSquareIcon,
} as const
